import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth';
import { useMyAccess } from '../../hooks/useMyAccess';
import { AccessErrorCard, FullPageSpinner, NotAMemberCard, PendingApprovalCard } from './AccessCards';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * The gate of `/admin` (PLAN-MULTI-TENANT § 10.2, lot L6g): a signed-in account whose profile is approved and who is a
 * member of the team, as `my_access()` says it. Which screen they may open inside is the layout's `ScreenGate`; what
 * they read there, the rules of the base (lot L6e). Being an administrator is no longer required to enter.
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading, signOut } = useAuth();
  const { state, retry } = useMyAccess();
  const location = useLocation();
  const [showLoading, setShowLoading] = useState(true);

  useEffect(() => {
    if (!loading) setShowLoading(false);
    // The session's spinner waits three seconds at most.
    const timeout = setTimeout(() => setShowLoading(false), 3000);
    return () => clearTimeout(timeout);
  }, [loading]);

  if (showLoading && loading) return <FullPageSpinner />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;

  const onSignOut = () => {
    void signOut();
  };
  if (user.is_approved === false) return <PendingApprovalCard onSignOut={onSignOut} />;

  switch (state.status) {
    case 'idle':
    case 'loading':
      return <FullPageSpinner />;
    case 'error':
      return <AccessErrorCard message={state.message} onRetry={retry} onSignOut={onSignOut} />;
    case 'ready':
      return state.access.isMember ? <>{children}</> : <NotAMemberCard onSignOut={onSignOut} />;
  }
};
